import { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { graphRequestScopes } from "../auth/scopes";
import { errorDev } from '@/logging/devConsole';

// Hook to detect browser color scheme
function usePrefersDarkMode() {
  const [isDark, setIsDark] = useState(
    () =>
      window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false
  );

  useEffect(() => {
    if (!window.matchMedia) return;

    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);

    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    } else {
      mql.addListener(handler);
      return () => mql.removeListener(handler);
    }
  }, []);

  return isDark;
}

// Choose the best logo URL based on theme and available assets
function selectBestLogo(
  branding: any,
  cdn?: string,
  darkMode = false
): string | null {
  const {
    squareLogoRelativeUrl,
    squareLogoDarkRelativeUrl,
    bannerLogoRelativeUrl,
    squareLogo,
    squareLogoDark,
    bannerLogo,
  } = branding;

  const mk = (rel?: string) => (rel && cdn ? `https://${cdn}/${rel}` : null);
  const candidates = darkMode
    ? [
        mk(squareLogoDarkRelativeUrl),
        mk(squareLogoRelativeUrl),
        squareLogoDark,
        squareLogo,
        bannerLogoRelativeUrl && mk(bannerLogoRelativeUrl),
        bannerLogo,
      ]
    : [
        mk(squareLogoRelativeUrl),
        mk(squareLogoDarkRelativeUrl),
        squareLogo,
        squareLogoDark,
        bannerLogoRelativeUrl && mk(bannerLogoRelativeUrl),
        bannerLogo,
      ];

  return candidates.find((u) => !!u) ?? null;
}

export function useTenantLogo() {
  const { accounts, instance } = useMsal();
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const isDarkMode = usePrefersDarkMode();

  useEffect(() => {
    if (!accounts?.[0]) {
      setLogoUrl(null);
      return;
    }

    const tenantId = accounts[0].tenantId;
    const theme = isDarkMode ? "dark" : "light";
    const cacheKey = `tenantLogoUrl_${tenantId}_${theme}`;
    const failureCacheKey = `tenantLogoFailed_${tenantId}`;

    // Check if we already cached a successful logo
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      setLogoUrl(cached);
      return;
    }

    // Check if we already tried and failed - don't retry
    const failureCached = sessionStorage.getItem(failureCacheKey);
    if (failureCached === 'true') {
      setLogoUrl(null); // Use Microsoft logo
      return;
    }

    (async () => {
      let result;
      try {
        // Try silent token acquisition
        result = await instance.acquireTokenSilent({
          scopes: graphRequestScopes,
          account: accounts[0],
        });
      } catch (silentError) {
        // If silent fails due to consent or interaction required, fall back to popup
        if (silentError instanceof InteractionRequiredAuthError) {
          try {
            result = await instance.acquireTokenPopup({
              scopes: graphRequestScopes,
              account: accounts[0],
            });
          } catch (popupError) {
            errorDev('Interactive token acquisition failed:', popupError);
            // Cache the failure so we don't retry on every screen
            sessionStorage.setItem(failureCacheKey, 'true');
            return;
          }
        } else {
          errorDev('Failed to acquire token silently:', silentError);
          // Cache the failure so we don't retry on every screen
          sessionStorage.setItem(failureCacheKey, 'true');
          return;
        }
      }

      try {
        const resp = await fetch(
          `https://graph.microsoft.com/v1.0/organization/${tenantId}/branding/localizations/default`,
          { headers: { Authorization: `Bearer ${result.accessToken}` } }
        );
        if (!resp.ok) throw new Error(resp.statusText);
        const data = await resp.json();
        const cdn = data.cdnList?.[0];
        const url = selectBestLogo(data, cdn, isDarkMode);
        if (url) {
          sessionStorage.setItem(cacheKey, url);
          setLogoUrl(url);
        } else {
          // No logo found, cache the failure
          sessionStorage.setItem(failureCacheKey, 'true');
        }
      } catch (fetchError) {
        errorDev('Failed to fetch tenant logo:', fetchError);
        // Cache the failure so we don't retry on every screen
        sessionStorage.setItem(failureCacheKey, 'true');
      }
    })();
  }, [accounts, instance, isDarkMode]);

  return logoUrl;
}
